import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * UI Store — all client-side state that should survive component unmounts
 * but doesn't need to be on the server.
 *
 * Conventions for using this store:
 * - Components read specific slices: useUIStore(s => s.activePillar)
 * - Components never read the whole store object (defeats the purpose of
 *   selector-based re-renders)
 * - State that's derived from server data does NOT go here — that's what
 *   TanStack Query's cache is for
 *
 * Persistence:
 * - Wrapped in `persist` so activePillar etc. survives page reload.
 *   Stored under localStorage key 'ct-q2-goals-v2-ui'.
 */
export const useUIStore = create(
  persist(
    (set) => ({
      // Which pillar tab is currently active (index into PILLARS array)
      activePillar: 0,
      setActivePillar: (i) => set({ activePillar: i }),

      // Which user is currently signed in (set after name-picker modal)
      // For now we just persist the name; auth comes later if needed
      currentUser: null,
      setCurrentUser: (name) => set({ currentUser: name }),

      // Hover state — example of UI-only state that used to live in DOM
      hoveredGoalKey: null,
      setHoveredGoalKey: (key) => set({ hoveredGoalKey: key }),

      // Comment panel — which context is open in the slide-in panel
      // Shape: { label: string, type: 'Goal' | 'Sub-Goal' | 'General' } or null
      activeCommentContext: null,
      openCommentPanel: (ctx) => set({ activeCommentContext: ctx }),
      closeCommentPanel: () => set({ activeCommentContext: null }),

      // Which goal is currently being edited by this user (for per-goal
      // presence chips). Shape: "pillarName:goalIndex" or null.
      editingGoalKey: null,
      setEditingGoalKey: (key) => set({ editingGoalKey: key }),
      clearEditingGoalKey: () => set({ editingGoalKey: null }),

      // Which DM thread is open in the docked popout panel.
      // Shape: { participants: [name, ...] } — participants is the list of
      // OTHER people in the thread (excludes currentUser). For a 1-on-1
      // this is [otherName]; for a group, two or more names.
      openDm: null,
      openDmWith: (nameOrArray) =>
        set((s) => {
          const names = Array.isArray(nameOrArray) ? nameOrArray : [nameOrArray]
          const others = names.filter((n) => n && n !== s.currentUser)
          if (others.length === 0) return s
          return { openDm: { participants: others } }
        }),
      closeDm: () => set({ openDm: null }),

      // Seen counts per DM thread — used to compute unread badges.
      // Shape: { [threadKey]: messagesLength }
      dmSeen: {},
      markDmSeen: (threadKey, count) =>
        set((s) => {
          // Idempotent: no state change if the count is already what we'd set.
          // Prevents infinite re-renders when a consumer wraps this in an
          // effect that depends on the seen count.
          if (s.dmSeen[threadKey] === count) return s
          return { dmSeen: { ...s.dmSeen, [threadKey]: count } }
        }),

      // Weekly archive panel — whether it's open, and which week is selected.
      // Shape: string weekKey (e.g. "2026-04-14") or null for closed.
      archiveWeekKey: null,
      openArchive: (weekKey) => set({ archiveWeekKey: weekKey || 'latest' }),
      closeArchive: () => set({ archiveWeekKey: null }),
      setArchiveWeek: (weekKey) => set({ archiveWeekKey: weekKey }),

      // Notification center / inbox panel
      notifOpen: false,
      openNotifs: () => set({ notifOpen: true }),
      closeNotifs: () => set({ notifOpen: false }),
      toggleNotifs: () => set((s) => ({ notifOpen: !s.notifOpen })),

      // Per-user persisted set of reply IDs the user has seen (i.e. for
      // @mention notifications). Shape: { [replyId]: true }
      mentionSeen: {},
      markMentionSeen: (replyId) =>
        set((s) => {
          if (!replyId || s.mentionSeen[replyId]) return s
          return { mentionSeen: { ...s.mentionSeen, [replyId]: true } }
        }),
      markAllMentionsSeen: (replyIds) =>
        set((s) => {
          const next = { ...s.mentionSeen }
          for (const id of replyIds || []) next[id] = true
          return { mentionSeen: next }
        }),
    }),
    {
      name: 'ct-q2-goals-v2-ui',
      // Don't persist hover state (it's transient)
      partialize: (state) => ({
        activePillar: state.activePillar,
        currentUser: state.currentUser,
        dmSeen: state.dmSeen,
        mentionSeen: state.mentionSeen,
      }),
    }
  )
)
